import { useEffect, useMemo, useRef, useState } from "react";
import { posthog } from "posthog-js";

import showdown from "showdown";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { cn } from "@/lib/utils";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight } from "lucide-react";

import { Form, FormControl, FormField } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import {
  ChatMessage,
  useVenturiPublicChatQuery,
} from "@/queries/venturi/venturi-public-chat";

import CompanyLogo1 from "@/assets/companies/healthgrades.svg";
import CompanyLogo2 from "@/assets/companies/bluecross_blueshield.svg";
import CompanyLogo3 from "@/assets/companies/allscripts.svg";
import CompanyLogo4 from "@/assets/companies/sutter_health.svg";
import { useSendVenturiPublicChatMessage } from "@/mutations/venturi/send-venturi-public-chat";

const CompanyLanding = () => {
  const [chatId] = useState(uuidv4());

  const { venturiPublicChat } = useVenturiPublicChatQuery(chatId);

  const chatOnly = Boolean(venturiPublicChat?.length);

  return (
    <div className="h-full mx-[-24px] bg-[linear-gradient(to_right,#00372E,#2C5C54,#00372E)] overflow-auto">
      <div className="min-h-0 p-3 md:w-[640px] flex-1 w-fit m-auto flex flex-col items-center justify-center gap-3">
        <div
          className={cn(
            "p-4 rounded-[80px] border border-white bg-[linear-gradient(180deg,_rgba(255,255,255,0.2)_0%,_rgba(255,255,255,0.06)_100%)]",
            "transition-all duration-500 ease-in-out transform",
            "text-center",
            chatOnly
              ? "opacity-0 scale-95 max-h-0 p-0 overflow-hidden"
              : "opacity-100 scale-100 max-h-[500px]",
          )}
        >
          <span className="text-lg md:text-xl leading-none text-white font-bold text-center">
            For Startups & Scaleups
          </span>
        </div>
        <h1
          className={cn(
            "text-2xl md:text-8xl text-transparent font-extrabold text-center",
            "bg-[linear-gradient(92deg,#FFF_43.27%,rgba(255,255,255,0.20)_97.37%)]",
            "bg-clip-text",
            "transition-all duration-500 ease-in-out transform",
            chatOnly
              ? "opacity-0 scale-95 max-h-0 overflow-hidden"
              : "opacity-100 scale-100 max-h-[500px]",
          )}
        >
          Find Your
          <br />
          Next Hire
          <br />
          In Seconds
        </h1>
        <div
          className={cn(
            "text-2xl text-white font-medium text-center",
            !chatOnly && "max-w-[610px]",
          )}
        >
          Tell us what you need and let our AI deliver top talent— fast, vetted
          by humans, and ready to go.
        </div>
        <div
          className={cn(
            "w-full flex flex-col gap-1",
            chatOnly && "min-h-0 flex-1",
          )}
        >
          <Chat chatId={chatId} chatMessages={venturiPublicChat} />
          <span className="text-sm text-white opacity-70 font-medium">
            No Credit Card Required.
          </span>
        </div>
        <div className="flex flex-col gap-3">
          <span className="text-base font-medium text-white opacity-65 text-center">
            Our executives have worked at
          </span>
          <div className="relative w-full overflow-hidden flex flex-row items-center justify-between max-h-8 gap-8">
            <img src={CompanyLogo1} alt="Healthgrades" />
            <img src={CompanyLogo2} alt="BlueCross BlueShield" />
            <img src={CompanyLogo3} alt="Allscripts" />
            <img src={CompanyLogo4} alt="Sutter Health" />
          </div>
        </div>
      </div>
    </div>
  );
};

const inputSchema = z.object({
  message: z.string({ required_error: "Note is required" }),
});

type InputSchema = z.infer<typeof inputSchema>;

const converter = new showdown.Converter({
  tables: true,
  strikethrough: true,
  tasklists: true,
  simpleLineBreaks: true,
});

const Chat = ({
  chatId,
  chatMessages,
}: {
  chatId: string;
  chatMessages: ChatMessage[] | null;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const { sendVenturiPublicChatMessage, isSendingVenturiPublicChatMessage } =
    useSendVenturiPublicChatMessage(chatId);

  // Convert messages from markdown to HTML
  const messages = useMemo(() => {
    if (!chatMessages) {
      return [];
    }

    return chatMessages.map(({ from, message }, idx) => {
      const rawHtml = converter.makeHtml(message);
      const sanitizedHtml = DOMPurify.sanitize(rawHtml);
      const jsx = parse(sanitizedHtml);

      return (
        <div
          key={`venturi-chat-${idx}`}
          className={cn(
            "chat-message",
            from === "Bot" ? "chat-bot-message" : "chat-user-message",
          )}
        >
          {jsx}
        </div>
      );
    });
  }, [chatMessages]);

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send a message
  const submitMessage = (message: string) => {
    if (!messages.length) {
      posthog.capture("hire_in_seconds_first_message");
    }

    sendVenturiPublicChatMessage(message);
    form.reset();
  };

  const form = useForm<InputSchema>({
    resolver: zodResolver(inputSchema),
    defaultValues: {
      message: "",
    },
  });

  const showMessages = Boolean(messages.length);

  return (
    <div
      className={cn(
        "p-3 flex flex-col gap-2 border border-[#d1d1d1] rounded-sm bg-white",
        "transition-all duration-500 ease-in-out",
        showMessages ? "flex-1 w-full" : "w-full md:w-[640px]",
      )}
    >
      {showMessages && (
        <div className={cn("min-h-0 p-5 flex-1 overflow-y-auto scroll-smooth")}>
          {messages}
          {isSendingVenturiPublicChatMessage && (
            <div className="chat-message chat-bot-message chat-typing-indicator">
              <span />
              <span />
              <span />
            </div>
          )}
          <div ref={ref} />
        </div>
      )}
      <Form {...form}>
        <form
          className={cn(
            "w-full flex flex-col gap-2",
            showMessages && "flex-row",
          )}
          onSubmit={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            void form.handleSubmit(({ message }) => submitMessage(message))();
          }}
        >
          <div className="w-full">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={
                      messages.length
                        ? undefined
                        : "I’m hiring a VP of Marketing who has 15 years of experience"
                    }
                    className={cn(
                      "resize-none",
                      !showMessages && "border-none",
                    )}
                    onSubmitPress={() =>
                      void form.handleSubmit(({ message }) =>
                        submitMessage(message),
                      )()
                    }
                  />
                </FormControl>
              )}
            />
          </div>
          <div className="flex self-end">
            <Button
              variant="default"
              disabled={
                !form.formState.isValid || !form.getValues("message").length
              }
            >
              {showMessages ? (
                "Send"
              ) : (
                <>
                  Try it Now
                  <ArrowRight />
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default CompanyLanding;
